import React, { useState } from "react";
import { Link2, Send, Copy, Check, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PACKAGES = ["Basic", "Classic", "Premium", "Luxury"];

/**
 * SendBookingLinkCard — ops tool to manually create + WhatsApp a secure
 * "complete your booking" link (address + advance payment) for a customer.
 * Mirrors the link the AI bot auto-issues; reuses POST /api/admin/booking-link/send.
 */
const SendBookingLinkCard = ({ authAxios }) => {
  const [form, setForm] = useState({ phone: "", name: "", date: "", setup_time: "", package: "Classic", occasion: "" });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (sendWhatsapp) => {
    setError(""); setResult(null);
    if (!form.phone || !form.date || !form.setup_time || !form.package || !form.occasion) {
      setError("Phone, date, setup time, package and occasion are required.");
      return;
    }
    setSending(true);
    try {
      const r = await authAxios.post(`${API_URL}/api/admin/booking-link/send`, { ...form, send_whatsapp: sendWhatsapp });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not create link.");
    }
    setSending(false);
  };

  const copy = () => {
    if (!result?.link) return;
    navigator.clipboard.writeText(result.link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const inputCls = "w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none text-sm";

  return (
    <div className="bg-white rounded-3xl shadow-lg p-6" data-testid="send-booking-link-card">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] flex items-center justify-center">
          <Link2 size={16} className="text-white" />
        </div>
        <div>
          <h3 className="text-base font-bold text-[#1E1B4B]">Send Booking Link</h3>
          <p className="text-xs text-[#94a3b8]">Create a secure link for a customer to confirm address & pay the advance on WhatsApp.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <input className={inputCls} placeholder="Customer phone (WhatsApp)" value={form.phone}
          onChange={(e) => set("phone", e.target.value.replace(/\D/g, "").slice(0, 12))} data-testid="sbl-phone" />
        <input className={inputCls} placeholder="Customer name (optional)" value={form.name}
          onChange={(e) => set("name", e.target.value)} data-testid="sbl-name" />
        <input className={inputCls} type="date" value={form.date}
          onChange={(e) => set("date", e.target.value)} data-testid="sbl-date" />
        <input className={inputCls} placeholder="Setup time (e.g. 6 PM)" value={form.setup_time}
          onChange={(e) => set("setup_time", e.target.value)} data-testid="sbl-time" />
        <select className={inputCls} value={form.package} onChange={(e) => set("package", e.target.value)} data-testid="sbl-package">
          {PACKAGES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <input className={inputCls} placeholder="Occasion (e.g. Birthday)" value={form.occasion}
          onChange={(e) => set("occasion", e.target.value)} data-testid="sbl-occasion" />
      </div>

      {error && <p className="text-red-500 text-sm mt-3" data-testid="sbl-error">{error}</p>}

      <div className="flex flex-wrap gap-2 mt-4">
        <button onClick={() => submit(true)} disabled={sending}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white font-bold text-sm disabled:opacity-50"
          data-testid="sbl-send-btn">
          {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Create & Send on WhatsApp
        </button>
        <button onClick={() => submit(false)} disabled={sending}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#1E1B4B]/20 text-[#1E1B4B] font-semibold text-sm hover:bg-[#1E1B4B]/5 disabled:opacity-50"
          data-testid="sbl-create-btn">
          <Link2 size={15} /> Create link only
        </button>
      </div>

      {result && (
        <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="sbl-result">
          <p className="text-xs text-emerald-700 font-semibold mb-1">
            Link created{result.whatsapp_sent ? " and sent on WhatsApp ✓" : ""}{result.reused ? " (existing draft reused)" : ""}
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs text-[#1E1B4B] bg-white rounded-lg px-2 py-1.5 truncate" data-testid="sbl-link">{result.link}</code>
            <button onClick={copy} className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50" data-testid="sbl-copy">
              {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} className="text-[#475569]" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SendBookingLinkCard;
